import { memo } from "react";
import { Box, Divider, IconButton, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  Close,
  DescriptionOutlined,
  Inventory2Outlined,
} from "@mui/icons-material";
import theme from "themeProvider";
import { formatEventName } from "../../eventIconMap";
import { formatDateTime } from "../../utils";
import {
  DetailRow,
  StatusPill,
  SectionHeader,
  ListItemNode,
  DetailLink,
} from "./DetailParts";

// Hover panel: template sections on the left, list items on the right.
const EventDetailsPanel = ({ event, Icon, color, details, onClose }) => (
  <Box
    sx={{
      width: 560,
      maxWidth: "90vw",
      maxHeight: 420,
      overflowY: "auto",
      background: theme.palette.common.white,
      border: `1px solid ${theme.palette.grey[300]}`,
      borderTop: `3px solid ${color}`,
      borderRadius: 2,
      boxShadow: "0 16px 40px rgba(16, 24, 40, 0.18)",
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        p: 1.5,
        borderBottom: `1px solid ${theme.palette.grey[200]}`,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: alpha(color, 0.12),
            flexShrink: 0,
          }}
        >
          <Icon sx={{ fontSize: 18, color }} />
        </Box>
        <Box>
          <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
            {formatEventName(event.eventName) || "Event"}
          </Typography>
          <Typography sx={{ fontSize: 11, color: theme.palette.text.secondary }}>
            {formatDateTime(event.receivedAt, "DD MMM YYYY, hh:mm:ss A")}
          </Typography>
        </Box>
      </Box>
      <IconButton size="small" onClick={onClose} aria-label="Close">
        <Close sx={{ fontSize: 16 }} />
      </IconButton>
    </Box>

    <Box sx={{ display: "flex", alignItems: "flex-start" }}>
      <Box sx={{ flex: 1, minWidth: 0, p: 1.5 }}>
        {details.link && (
          <DetailRow
            label={details.link.label}
            value={<DetailLink link={details.link} />}
          />
        )}
        {details.sections.map((section, index) => (
          <Box
            key={`${section.title}-${index}`}
            sx={{ mt: index || details.link ? 1.5 : 0 }}
          >
            <SectionHeader
              icon={section.icon || DescriptionOutlined}
              title={section.title}
            />
            {section.rows.map((row, rowIndex) => (
              <DetailRow
                key={`${row.label}-${rowIndex}`}
                label={row.label}
                value={
                  row.type === "status" ? (
                    <StatusPill label={row.value} color={color} />
                  ) : (
                    row.value
                  )
                }
              />
            ))}
          </Box>
        ))}
      </Box>

      {details.list && (
        <>
          <Divider orientation="vertical" flexItem sx={{ my: 1.5 }} />
          <Box sx={{ flex: 1, minWidth: 0, p: 1.5 }}>
            <SectionHeader
              icon={details.list.icon || Inventory2Outlined}
              title={`${details.list.title} (${details.list.items.length})`}
            />
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {details.list.items.map((item, index) => (
                <ListItemNode
                  key={`${item.primary}-${index}`}
                  item={item}
                  color={color}
                />
              ))}
            </Box>
          </Box>
        </>
      )}
    </Box>
  </Box>
);

export default memo(EventDetailsPanel);
